"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import type { TypedNotification } from "@/types/feed.types";
import type { NotificationFilter } from "../NotificationsCauseFilter";
import { useSetTitle } from "@/contexts/TitleContext";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useNotificationsQuery } from "@/hooks/useNotificationsQuery";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { useLayout } from "../../my-stream/layout/LayoutContext";
import { commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { DEFAULT_ERROR_MESSAGE, LOAD_TIMEOUT_MESSAGE, LOAD_TIMEOUT_MS } from "../utils/constants";
import { getNotificationErrorDetails } from "../utils/getNotificationErrorDetails";
interface NotificationsContentState {
  readonly isLoadingProfile: boolean;
  readonly hasConnectedProfile: boolean;
  readonly hasProfileHandle: boolean;
  readonly showProxyDisabledState: boolean;
  readonly showErrorState: boolean;
  readonly resolvedErrorMessage: string;
  readonly showLoader: boolean;
  readonly showNoItems: boolean;
}
interface NotificationsHandlers {
  readonly handleRetry: () => void;
  readonly handleAuthRetry: () => void;
  readonly handleProxyDisable: () => void;
}
interface NotificationsPagination {
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
}
interface UseNotificationsControllerResult {
  readonly activeFilter: NotificationFilter | null;
  readonly setActiveFilter: (filter: NotificationFilter | null) => void;
  readonly isAuthenticated: boolean;
  readonly notificationsViewStyle: CSSProperties;
  readonly items: TypedNotification[];
  readonly isFetchingNextPage: boolean;
  readonly pagination: NotificationsPagination;
  readonly contentState: NotificationsContentState;
  readonly handlers: NotificationsHandlers;
}
export const useNotificationsController = () => {
  const $ = _c(80);
  const {
    connectedProfile,
    activeProfileProxy,
    fetchingProfile,
    requestAuth,
    setToast,
    setActiveProfileProxy
  } = useContext(AuthContext);
  const {
    notificationsViewStyle
  } = useLayout();
  const {
    removeAllDeliveredNotifications
  } = useNotificationsContext();
  const {
    invalidateNotifications
  } = useContext(ReactQueryWrapperContext);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasMarkedAllAsReadRef = useRef(false);
  const errorToastShownRef = useRef(false);
  const reauthTriggeredRef = useRef(false);
  const timeoutToastShownRef = useRef(false);
  const lastErrorMessageRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get("reload") ?? undefined;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const reload = t0;
  const isAuthenticated = !!connectedProfile?.handle && !activeProfileProxy;
  const isLoadingProfile = fetchingProfile && !connectedProfile;
  const hasConnectedProfile = !!connectedProfile;
  const hasProfileHandle = !!connectedProfile?.handle;
  useSetTitle("Notifications | My Stream | Brain");
  let t1;
  if ($[2] !== invalidateNotifications || $[3] !== removeAllDeliveredNotifications) {
    t1 = async () => {
      ;
      try {
        invalidateNotifications();
        await removeAllDeliveredNotifications();
      } catch (t2) {
        const error = t2;
        console.error("Failed to clear delivered notifications:", error);
      }
    };
    $[2] = invalidateNotifications;
    $[3] = removeAllDeliveredNotifications;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== setToast) {
    t2 = error_0 => {
      setToast({
        message: error_0 instanceof Error ? error_0.message : String(error_0),
        type: "error"
      });
    };
    $[5] = setToast;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: _temp,
      onSuccess: t1,
      onError: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    mutateAsync: markAllAsRead
  } = useMutation(t3);
  let t4;
  let t5;
  if ($[10] !== isAuthenticated || $[11] !== markAllAsRead || $[12] !== reload) {
    t4 = () => {
      if (!isAuthenticated) {
        return;
      }
      if (reload === "true" || hasMarkedAllAsReadRef.current) {
        return;
      }
      hasMarkedAllAsReadRef.current = true;
      markAllAsRead().catch(_temp2);
    };
    t5 = [isAuthenticated, markAllAsRead, reload];
    $[10] = isAuthenticated;
    $[11] = markAllAsRead;
    $[12] = reload;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] !== isAuthenticated) {
    t6 = () => {
      if (!isAuthenticated) {
        hasMarkedAllAsReadRef.current = false;
      }
    };
    t7 = [isAuthenticated];
    $[15] = isAuthenticated;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  const t8 = isAuthenticated ? connectedProfile?.handle : undefined;
  const t9 = !!activeProfileProxy;
  const t10 = activeFilter?.cause;
  let t11;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = {
      identity: t8,
      activeProfileProxy: t9,
      limit: "30",
      reverse: true,
      cause: t10
    };
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const {
    items,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    isInitialQueryDone,
    isSuccess,
    error: queryError
  } = useNotificationsQuery(t11);
  let t12;
  let t13;
  if ($[22] !== isAuthenticated || $[23] !== markAllAsRead || $[24] !== pathname || $[25] !== refetch || $[26] !== reload || $[27] !== router || $[28] !== searchParams) {
    t12 = () => {
      if (reload !== "true") {
        return;
      }
      const clearReloadParam = () => {
        const params = new URLSearchParams(searchParams?.toString() || "");
        params.delete("reload");
        const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || "/my-stream/notifications";
        router.replace(newUrl, {
          scroll: false
        });
      };
      if (!isAuthenticated) {
        clearReloadParam();
        return;
      }
      refetch().then(() => {
        hasMarkedAllAsReadRef.current = true;
        return markAllAsRead();
      }).catch(_temp3).finally(() => {
        clearReloadParam();
      });
    };
    t13 = [pathname, isAuthenticated, markAllAsRead, refetch, reload, router, searchParams];
    $[22] = isAuthenticated;
    $[23] = markAllAsRead;
    $[24] = pathname;
    $[25] = refetch;
    $[26] = reload;
    $[27] = router;
    $[28] = searchParams;
    $[29] = t12;
    $[30] = t13;
  } else {
    t12 = $[29];
    t13 = $[30];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[31] !== queryError || $[32] !== requestAuth || $[33] !== setToast) {
    t14 = () => {
      if (!queryError) {
        setErrorMessage(null);
        setHasTimedOut(false);
        errorToastShownRef.current = false;
        reauthTriggeredRef.current = false;
        lastErrorMessageRef.current = null;
        return;
      }
      const {
        message,
        isUnauthorized
      } = getNotificationErrorDetails(queryError);
      if (lastErrorMessageRef.current !== message) {
        errorToastShownRef.current = false;
        reauthTriggeredRef.current = false;
        lastErrorMessageRef.current = message;
      }
      setErrorMessage(message);
      setHasTimedOut(false);
      if (!errorToastShownRef.current) {
        setToast({
          message,
          type: "error"
        });
        errorToastShownRef.current = true;
      }
      if (isUnauthorized && !reauthTriggeredRef.current) {
        requestAuth().catch(_temp4);
        reauthTriggeredRef.current = true;
      }
    };
    t15 = [queryError, requestAuth, setToast];
    $[31] = queryError;
    $[32] = requestAuth;
    $[33] = setToast;
    $[34] = t14;
    $[35] = t15;
  } else {
    t14 = $[34];
    t15 = $[35];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[36] !== errorMessage || $[37] !== isAuthenticated || $[38] !== isInitialQueryDone || $[39] !== isLoadingProfile || $[40] !== isSuccess) {
    t16 = () => {
      if (isSuccess) {
        setHasTimedOut(false);
        timeoutToastShownRef.current = false;
        return;
      }
      if (errorMessage || !isAuthenticated || isLoadingProfile) {
        setHasTimedOut(false);
        timeoutToastShownRef.current = false;
        return;
      }
      if (isInitialQueryDone) {
        return;
      }
      const timerId = globalThis.setTimeout(() => {
        setHasTimedOut(true);
      }, LOAD_TIMEOUT_MS);
      return () => {
        globalThis.clearTimeout(timerId);
      };
    };
    t17 = [errorMessage, isAuthenticated, isInitialQueryDone, isLoadingProfile, isSuccess];
    $[36] = errorMessage;
    $[37] = isAuthenticated;
    $[38] = isInitialQueryDone;
    $[39] = isLoadingProfile;
    $[40] = isSuccess;
    $[41] = t16;
    $[42] = t17;
  } else {
    t16 = $[41];
    t17 = $[42];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[43] !== hasTimedOut || $[44] !== setToast) {
    t18 = () => {
      if (hasTimedOut) {
        if (!timeoutToastShownRef.current) {
          setToast({
            message: LOAD_TIMEOUT_MESSAGE,
            type: "warning"
          });
          timeoutToastShownRef.current = true;
        }
      } else {
        timeoutToastShownRef.current = false;
      }
    };
    t19 = [hasTimedOut, setToast];
    $[43] = hasTimedOut;
    $[44] = setToast;
    $[45] = t18;
    $[46] = t19;
  } else {
    t18 = $[45];
    t19 = $[46];
  }
  useEffect(t18, t19);
  let t20;
  if ($[47] !== refetch) {
    t20 = () => {
      setHasTimedOut(false);
      setErrorMessage(null);
      errorToastShownRef.current = false;
      reauthTriggeredRef.current = false;
      lastErrorMessageRef.current = null;
      refetch({
        cancelRefetch: true
      }).catch(_temp5);
    };
    $[47] = refetch;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const handleRetry = t20;
  let t21;
  if ($[49] !== requestAuth || $[50] !== setToast) {
    t21 = () => {
      requestAuth().catch(error_5 => {
        console.error("Failed to re-authenticate:", error_5);
        setToast({
          message: error_5 instanceof Error ? error_5.message : DEFAULT_ERROR_MESSAGE,
          type: "error"
        });
      });
    };
    $[49] = requestAuth;
    $[50] = setToast;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const handleAuthRetry = t21;
  let t22;
  if ($[52] !== setActiveProfileProxy || $[53] !== setToast) {
    t22 = () => {
      setActiveProfileProxy(null).catch(error_6 => {
        console.error("Failed to switch to primary profile:", error_6);
        setToast({
          message: error_6 instanceof Error ? error_6.message : "Unable to switch to primary profile. Please try again.",
          type: "error"
        });
      });
    };
    $[52] = setActiveProfileProxy;
    $[53] = setToast;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  const handleProxyDisable = t22;
  const showLoader = isAuthenticated && !hasTimedOut && !errorMessage && (!isInitialQueryDone || isFetching) && items.length === 0;
  const showNoItems = isAuthenticated && !errorMessage && !hasTimedOut && isInitialQueryDone && !isFetching && items.length === 0;
  const showErrorState = (!!errorMessage || hasTimedOut) && items.length === 0;
  const showProxyDisabledState = !!activeProfileProxy;
  const resolvedErrorMessage = hasTimedOut ? LOAD_TIMEOUT_MESSAGE : errorMessage ?? DEFAULT_ERROR_MESSAGE;
  let t23;
  if ($[55] !== hasConnectedProfile || $[56] !== hasProfileHandle || $[57] !== isLoadingProfile || $[58] !== resolvedErrorMessage || $[59] !== showErrorState || $[60] !== showLoader || $[61] !== showNoItems || $[62] !== showProxyDisabledState) {
    t23 = {
      isLoadingProfile,
      hasConnectedProfile,
      hasProfileHandle,
      showProxyDisabledState,
      showErrorState,
      resolvedErrorMessage,
      showLoader,
      showNoItems
    };
    $[55] = hasConnectedProfile;
    $[56] = hasProfileHandle;
    $[57] = isLoadingProfile;
    $[58] = resolvedErrorMessage;
    $[59] = showErrorState;
    $[60] = showLoader;
    $[61] = showNoItems;
    $[62] = showProxyDisabledState;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  const contentState = t23;
  let t24;
  if ($[64] !== handleAuthRetry || $[65] !== handleProxyDisable || $[66] !== handleRetry) {
    t24 = {
      handleRetry,
      handleAuthRetry,
      handleProxyDisable
    };
    $[64] = handleAuthRetry;
    $[65] = handleProxyDisable;
    $[66] = handleRetry;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  const handlers = t24;
  const t25 = !!hasNextPage;
  let t26;
  if ($[68] !== fetchNextPage || $[69] !== t25) {
    t26 = {
      hasNextPage: t25,
      fetchNextPage
    };
    $[68] = fetchNextPage;
    $[69] = t25;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  const pagination = t26;
  let t27;
  if ($[71] !== activeFilter || $[72] !== contentState || $[73] !== handlers || $[74] !== isAuthenticated || $[75] !== isFetchingNextPage || $[76] !== items || $[77] !== notificationsViewStyle || $[78] !== pagination) {
    t27 = {
      activeFilter,
      setActiveFilter,
      isAuthenticated,
      notificationsViewStyle,
      items,
      isFetchingNextPage,
      pagination,
      contentState,
      handlers
    };
    $[71] = activeFilter;
    $[72] = contentState;
    $[73] = handlers;
    $[74] = isAuthenticated;
    $[75] = isFetchingNextPage;
    $[76] = items;
    $[77] = notificationsViewStyle;
    $[78] = pagination;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  return t27;
};
async function _temp() {
  return await commonApiPostWithoutBodyAndResponse({
    endpoint: "notifications/read"
  });
}
function _temp2(error_1) {
  console.error("Failed to mark notifications as read:", error_1);
}
function _temp3(error_2) {
  console.error("Error during refetch:", error_2);
}
function _temp4(error_3) {
  console.error("Failed to re-authenticate after notifications error:", error_3);
}
function _temp5(error_4) {
  console.error("Failed to retry notifications fetch:", error_4);
}